/**
 * What can be made at a station.
 *
 * Opened by the station's own action, and showing everything it makes: what
 * comes out, what goes in, what it asks of you, and how many you could make
 * right now. One of them can be set as what a left click on that kind of station
 * does from then on, one at a time or until the materials run out.
 */

import type { ItemStack } from '@inverse/common';
import type { ItemDefinitionWire, RecipeWire } from '@inverse/protocol';
import { CloseButton } from './CloseButton';
import { useDraggableWindow } from './draggable';
import { useAltHeld } from './alt-mode';
import { useWindowGrips } from './grips';
import { useState } from 'react';
import { couldMake, loadCounts, recipeState, saveCount, type StationDefault } from './crafting';
import { Explain } from './Explain';

export function Crafting({
  station,
  recipes,
  bag,
  items,
  levels,
  progress,
  chosen,
  onCraft,
  onDefault,
  onClose,
}: {
  /** The station itself: what it is called and where it stands. */
  station: { objectId: number; name: string; tx: number; tz: number; plane: number };
  recipes: readonly RecipeWire[];
  bag: readonly (ItemStack | null)[];
  items: readonly ItemDefinitionWire[];
  /** Levels by skill name, for saying what is out of reach. */
  levels: Readonly<Record<string, number>>;
  /**
   * How far along the quests are, and what flags are held.
   *
   * A recipe can be gated on something learned rather than on a level, and one
   * nobody has been taught is not shown at all until they are.
   */
  progress: {
    quests: readonly { id: string; step: number }[];
    flags: readonly { name: string; value: string }[];
  };
  /** What this kind of station has been told to make, if anything. */
  chosen: StationDefault | undefined;
  /** Which recipe as well as how many, since one entry can start several. */
  onCraft: (action: string, count: number, outputId: number) => void;
  onDefault: (held: StationDefault | null) => void;
  onClose: () => void;
}): JSX.Element {
  const { attach, style, onPointerDown, nudge } = useDraggableWindow('crafting');
  const altHeld = useAltHeld();
  const { size: windowSize, grips } = useWindowGrips('crafting', nudge, altHeld, {
    width: 300,
    height: 220,
  });

  const nameOf = (id: number): string => items.find((item) => item.id === id)?.name ?? `Item ${id}`;

  /** The number somebody typed in here last time, by kind of station. */
  const [counts, setCounts] = useState<Record<number, number>>(loadCounts);

  /** What this station makes that this player has been taught to make. */
  const known = recipes.filter((recipe) => !recipeState(recipe, levels, progress).unknown);

  /**
   * What can be made now, and what cannot, each from the lowest level up.
   *
   * Two lists rather than one with the reachable ones marked: somebody at a
   * station is deciding what to make out of what they can make, and the rest
   * is a wish list they read afterwards.
   */
  const byLevel = (a: RecipeWire, b: RecipeWire): number =>
    a.level - b.level || nameOf(a.outputId).localeCompare(nameOf(b.outputId));

  const within = known
    .filter((recipe) => recipeState(recipe, levels, progress).short.length === 0)
    .sort(byLevel);
  const beyond = known
    .filter((recipe) => recipeState(recipe, levels, progress).short.length > 0)
    .sort(byLevel);

  /**
   * Which one is being read.
   *
   * Whatever this station was told to make by default, or the first thing that
   * can be made: opening a window onto nothing is opening it onto a question.
   */
  const [picked, setLooking] = useState<string | null>(null);
  const looking = picked ?? chosen?.action ?? within[0]?.action ?? beyond[0]?.action ?? null;
  const open = known.find((recipe) => recipe.action === looking) ?? null;

  return (
    <div
      className={`trade crafting${altHeld ? ' ui-mode' : ''}${windowSize ? ' sized' : ''}`}
      ref={attach}
      style={{ ...style, ...(windowSize ?? {}) }}
    >
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>{station.name}</span>
        <CloseButton onClose={onClose} />
      </div>

      <div className="panel-body crafting-body">
        {/* Anything nobody has been taught is not here at all: a window full of
            things that cannot be made and will not say why is a window that
            teaches somebody the game is broken. */}
        {known.length === 0 ? <p className="hint">Nothing is made here.</p> : null}

        {known.length > 0 ? (
          <>
            {/* The list down one side, in the order somebody meets it, and what
                is chosen filling the rest. A column of a dozen fully written out
                recipes is a column nobody reads to the end of. */}
            <div className="craft-list">
              {[
                { title: 'Can Craft', rows: within },
                { title: 'Cannot Craft', rows: beyond },
              ]
                .filter((group) => group.rows.length > 0)
                .map((group) => (
                  <div className="craft-group" key={group.title}>
                    <h4>{group.title}</h4>

                    {group.rows.map((recipe) => {
                      const state = recipeState(recipe, levels, progress);
                      const many = couldMake(recipe, bag);

                      return (
                        <button
                          key={recipe.action}
                          type="button"
                          className={[
                            'craft-row',
                            recipe.action === looking ? 'on' : '',
                            state.short.length > 0 ? 'short' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => setLooking(recipe.action)}
                        >
                          <span className="craft-row-level">{recipe.level}</span>
                          <span className="craft-row-name">{nameOf(recipe.outputId)}</span>

                          {/* The one a left click here makes, and whether that
                              means one of them or as many as the bag allows. */}
                          {chosen?.action === recipe.action ? (
                            <span
                              className="craft-row-star"
                              title={
                                chosen.all
                                  ? 'A left click here keeps going until the materials run out'
                                  : 'A left click here makes one'
                              }
                            >
                              <span className="craft-star" aria-hidden />
                              <b>{chosen.all ? '\u00d7' : '1'}</b>
                            </span>
                          ) : (
                            <span className="craft-row-count">{many > 0 ? many : ''}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
            </div>

            <div className="craft-detail">
              {open ? (
                <Recipe
                  recipe={open}
                  bag={bag}
                  levels={levels}
                  progress={progress}
                  chosen={chosen}
                  station={station}
                  counts={counts}
                  nameOf={nameOf}
                  onCount={(count) => {
                    saveCount(station.objectId, count);
                    setCounts((held) => ({ ...held, [station.objectId]: count }));
                  }}
                  onCraft={onCraft}
                  onDefault={onDefault}
                />
              ) : null}
            </div>
          </>
        ) : null}
      </div>
      {grips}
    </div>
  );
}

/**
 * Everything about one recipe, for the side that is read rather than scanned.
 *
 * A component in its own right rather than a function inside the window. React
 * takes a function declared inside a render to be a different component every
 * time that render runs, so this used to be torn down and built again on every
 * keystroke, every packet and every hover: the buttons in it flickered, lost the
 * pointer, and swallowed the click that was already on its way to them.
 */
function Recipe({
  recipe,
  bag,
  levels,
  progress,
  chosen,
  station,
  counts,
  nameOf,
  onCount,
  onCraft,
  onDefault,
}: {
  recipe: RecipeWire;
  bag: readonly (ItemStack | null)[];
  levels: Readonly<Record<string, number>>;
  progress: {
    quests: readonly { id: string; step: number }[];
    flags: readonly { name: string; value: string }[];
  };
  chosen: StationDefault | undefined;
  station: { objectId: number };
  counts: Record<number, number>;
  nameOf: (id: number) => string;
  onCount: (count: number) => void;
  onCraft: (action: string, count: number, outputId: number) => void;
  onDefault: (held: StationDefault | null) => void;
}): JSX.Element {
  const many = couldMake(recipe, bag);
  const state = recipeState(recipe, levels, progress);
  const canReach = state.short.length === 0;
  const isDefault = chosen?.action === recipe.action;

  return (
    <div className={isDefault ? 'recipe chosen' : 'recipe'}>
      <div className="recipe-head">
        <strong>{nameOf(recipe.outputId)}</strong>
        <span className="recipe-count">{many > 0 ? `${many} possible` : 'none'}</span>
      </div>

      <p className="recipe-needs">
        <span>
          {recipe.inputQuantity} x {nameOf(recipe.inputId)}
        </span>

        {/* Every skill it takes, each said whether or not it is reached, and
            in red when it is not. A recipe out of reach is worth showing: it
            is what the next levels are for. */}
        {[{ skill: recipe.skill, level: recipe.level }, ...recipe.alsoNeeds].map((need) => {
          const have = levels[need.skill] ?? 1;
          const met = have >= need.level;

          return (
            <span
              key={`${need.skill}-${need.level}`}
              className={met ? 'recipe-level' : 'recipe-level short'}
            >
              {titled(need.skill)} {need.level}
              {met ? '' : ` (you are ${have})`}
            </span>
          );
        })}
      </p>

      <CraftCounts
        recipe={recipe}
        many={many}
        canReach={canReach}
        custom={counts[station.objectId] ?? null}
        onCustom={onCount}
        onCraft={onCraft}
      />

      {/* What a left click on this kind of station does from now on. Set here
          rather than in the settings, because this is where somebody is
          standing when they decide. */}
      <div className="recipe-default">
        <span className="craft-label">Default</span>
        <button
          type="button"
          className={isDefault && chosen?.all === false ? 'editor-tab on' : 'editor-tab'}
          title="A left click here makes one"
          onClick={() =>
            onDefault(
              isDefault && chosen?.all === false ? null : { action: recipe.action, all: false },
            )
          }
        >
          One
        </button>
        <button
          type="button"
          className={isDefault && chosen?.all === true ? 'editor-tab on' : 'editor-tab'}
          title="A left click here keeps going until the materials run out"
          onClick={() =>
            onDefault(
              isDefault && chosen?.all === true ? null : { action: recipe.action, all: true },
            )
          }
        >
          All
        </button>
      </div>

      <p className="hint">
        A default says what a left click here makes.
        <Explain>
          The plain action is still there, one place down the menu, and so is this window. Using
          materials on the station starts the same work.
        </Explain>
      </p>
    </div>
  );
}

/** A skill as it is written where somebody reads it. */
function titled(name: string): string {
  return name.length > 0 ? (name[0]?.toUpperCase() ?? '') + name.slice(1) : name;
}

/**
 * How many to make: a few counts, and one that is whatever you say it is.
 *
 * The button that says how many is greyed when the bag cannot reach that many,
 * so the row itself says what is possible without anybody counting ore. The
 * last one starts as a question and becomes the number typed into it, which is
 * how every game with a crafting window has done this for twenty years.
 */
function CraftCounts({
  recipe,
  many,
  canReach,
  custom,
  onCustom,
  onCraft,
}: {
  recipe: RecipeWire;
  /** How many the bag has the materials for. */
  many: number;
  canReach: boolean;
  custom: number | null;
  onCustom: (count: number) => void;
  /** Which recipe as well as how many, since one entry can start several. */
  onCraft: (action: string, count: number, outputId: number) => void;
}): JSX.Element {
  const [asking, setAsking] = useState(false);
  const [typed, setTyped] = useState('');

  const count = (wanted: number, label: string): JSX.Element => (
    <button
      type="button"
      className="craft-count"
      disabled={!canReach || many < wanted}
      title={canReach ? `Make ${wanted}` : `Needs ${titled(recipe.skill)} ${recipe.level}`}
      onClick={() => onCraft(recipe.action, wanted, recipe.outputId)}
    >
      {label}
    </button>
  );

  const submit = (): void => {
    const held = Math.min(1000, Math.max(1, Math.round(Number(typed))));
    setAsking(false);
    if (!Number.isFinite(held)) return;

    onCustom(held);
    onCraft(recipe.action, held, recipe.outputId);
  };

  return (
    <div className="recipe-craft">
      <span className="craft-label">Craft</span>
      {count(1, '1x')}
      {count(5, '5x')}

      <button
        type="button"
        className="craft-count"
        disabled={!canReach || many < 1}
        title="Keep going until the materials run out"
        onClick={() => onCraft(recipe.action, 0, recipe.outputId)}
      >
        All
      </button>

      {asking ? (
        <span className="craft-ask">
          <input
            type="text"
            inputMode="numeric"
            autoFocus
            value={typed}
            placeholder="How many"
            onChange={(event) => setTyped(event.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
              if (event.key === 'Escape') setAsking(false);
            }}
          />
          <button type="button" className="craft-count" title="Make that many" onClick={submit}>
            ✓
          </button>
        </span>
      ) : (
        <button
          type="button"
          className="craft-count"
          disabled={custom !== null && (!canReach || many < custom)}
          title={custom === null ? 'Choose how many' : `Make ${custom}. Right click to change it.`}
          onClick={() => {
            if (custom === null) {
              setTyped('');
              setAsking(true);
              return;
            }

            onCraft(recipe.action, custom, recipe.outputId);
          }}
          onContextMenu={(event) => {
            event.preventDefault();
            setTyped(custom === null ? '' : String(custom));
            setAsking(true);
          }}
        >
          {custom === null ? '?x' : `${custom}x`}
        </button>
      )}
    </div>
  );
}
